import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import AlertsInbox from './AlertsInbox';
import AlertNotificationSettingsDialog from './AlertNotificationSettingsDialog';
import NotificationMessageModal from './notifications/NotificationMessageModal';
import { parseAnnouncementTimestamp, isInboxItemAnnouncement } from '../lib/announcements';

const MENU_MAX_ITEMS = 10;

export default function AlertsBellMenu({
  userData,
  alertsRemote,
  onViewAllAlerts,
  showAlertsOnOverview,
  onChangeShowAlertsOnOverview,
  onOpenAlertDetail,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [messageModalItem, setMessageModalItem] = useState(null);
  const open = Boolean(anchorEl);
  const unread = alertsRemote?.unreadInInbox ?? 0;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goToNotifications = () => {
    handleClose();
    onViewAllAlerts?.();
  };

  const messageModalCreatedAt = messageModalItem
    ? (isInboxItemAnnouncement(messageModalItem)
      ? parseAnnouncementTimestamp(messageModalItem.timestamp ?? messageModalItem.createdAt)
      : (messageModalItem.createdAt?.toDate?.() || new Date(messageModalItem.createdAt)))
    : null;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={`Notifications${unread > 0 ? `, ${unread} unread` : ''}`}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          size="medium"
        >
          <Badge
            color="warning"
            badgeContent={unread > 0 ? (unread > 99 ? '99+' : unread) : 0}
            invisible={unread === 0}
            overlap="circular"
          >
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxWidth: 'calc(100vw - 24px)',
              mt: 1,
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'min(80vh, 560px)',
            },
          },
        }}
      >
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5, flexShrink: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Your notifications
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Quick preview — open the full inbox in Settings for more.
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0, px: 1, py: 1 }}>
          <AlertsInbox
            userData={userData}
            showTitle={false}
            variant="menu"
            maxItems={MENU_MAX_ITEMS}
            alertsRemote={alertsRemote}
            onFocusedAlertChange={onOpenAlertDetail}
            onOpenedFromMenu={handleClose}
            onOpenMessageModal={setMessageModalItem}
          />
        </Box>
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ px: 1, py: 1, flexShrink: 0, bgcolor: 'action.hover' }}
        >
          <Button
            size="small"
            startIcon={<SettingsOutlinedIcon fontSize="small" />}
            onClick={() => setSettingsOpen(true)}
            color="inherit"
          >
            Settings
          </Button>
          <Button
            size="small"
            variant="contained"
            endIcon={<OpenInFullIcon sx={{ fontSize: 18 }} />}
            onClick={goToNotifications}
          >
            Open full inbox
          </Button>
        </Stack>
      </Popover>

      <AlertNotificationSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        showAlertsOnOverview={showAlertsOnOverview}
        onChangeShowAlertsOnOverview={onChangeShowAlertsOnOverview}
        userData={userData}
      />

      <NotificationMessageModal
        open={Boolean(messageModalItem)}
        item={messageModalItem}
        createdAt={messageModalCreatedAt}
        onClose={() => setMessageModalItem(null)}
      />
    </>
  );
}
